// Vault identity (program/src/state.rs): vault_id = keccak256("THEVAULT:ID:1" || genesis
// verifying key), address = PDA of ["vault", vault_id] under the program.

import { keccak_256 } from '@noble/hashes/sha3.js';
import { getAddressDecoder, getAddressEncoder, getProgramDerivedAddress, type Address } from '@solana/kit';
import { concat } from './derive.js';

export const VAULT_ID_TAG: Uint8Array = new TextEncoder().encode('THEVAULT:ID:1');
export const VAULT_SEED: Uint8Array = new TextEncoder().encode('vault');
/** root (23) || P (18). */
export const VERIFYING_KEY_LEN = 41;

export interface VaultAddress {
  readonly address: Address;
  readonly bump: number;
  readonly vaultId: Uint8Array;
}

export function vaultId(genesisKey: Uint8Array): Uint8Array {
  if (genesisKey.length !== VERIFYING_KEY_LEN) throw new RangeError(`genesis key must be ${VERIFYING_KEY_LEN} bytes`);
  return keccak_256(concat(VAULT_ID_TAG, genesisKey));
}

/** The canonical vault PDA for a genesis verifying key under `programId`. */
export async function vaultAddress(programId: Address, genesisKey: Uint8Array): Promise<VaultAddress> {
  const id = vaultId(genesisKey);
  const [address, bump] = await getProgramDerivedAddress({ programAddress: programId, seeds: [VAULT_SEED, id] });
  return { address, bump, vaultId: id };
}

export function addressBytes(address: Address): Uint8Array {
  return new Uint8Array(getAddressEncoder().encode(address));
}

export function addressFromBytes(bytes: Uint8Array): Address {
  return getAddressDecoder().decode(bytes);
}
